import { createSlice } from '@reduxjs/toolkit';
import {
  sameId, idKey, isSelfLoop, connectsPair, toLegacyEdge,
} from '../lib/model';

export const initialModelState = {
  meta: {},
  nodes: [],
  edges: [],
};

const dropEmptySize = (node) => {
  const next = { ...node };
  if (next.width == null) delete next.width;
  if (next.height == null) delete next.height;
  if (next.resized !== true) delete next.resized;
  return next;
};

const networkUndoSlice = createSlice({
  name: 'networkUndo',
  initialState: initialModelState,
  reducers: {
    setNetwork: (state, action) => action.payload,
    setNodes: (state, action) => {
      state.nodes = action.payload;
    },
    setEdges: (state, action) => {
      state.edges = action.payload;
    },
    addNode: (state, action) => {
      state.nodes.push(action.payload);
    },
    addEdge: (state, action) => {
      const newEdge = { ...action.payload };
      if (!isSelfLoop(newEdge) && typeof newEdge.roundness !== 'number') {
        newEdge.roundness = 0;
      }
      state.edges.push(newEdge);
    },
    updateNode: (state, action) => {
      const node = { ...action.payload };
      if ('oldId' in node) {
        const { oldId } = node;
        delete node.oldId;

        // re-point links, then swap the node
        state.edges.forEach((edge, index) => {
          if (sameId(edge.to, oldId)) state.edges[index].to = node.id;
          if (sameId(edge.from, oldId)) state.edges[index].from = node.id;
        });
        const index = state.nodes.findIndex((item) => sameId(item.id, oldId));
        if (index > -1) {
          state.nodes[index] = dropEmptySize({ ...state.nodes[index], ...node });
        } else {
          state.nodes.push(dropEmptySize(node));
        }
      } else {
        const index = state.nodes.findIndex((item) => sameId(item.id, node.id));
        if (index > -1) state.nodes[index] = dropEmptySize({ ...state.nodes[index], ...node });
      }
    },
    updateEdge: (state, action) => {
      const edge = action.payload;
      const index = state.edges.findIndex((item) => sameId(item.id, edge.id));
      if (index > -1) state.edges[index] = toLegacyEdge({ ...state.edges[index], ...edge });
    },
    removeNode: (state, action) => {
      const id = action.payload;
      const index = state.nodes.findIndex((item) => sameId(item.id, id));
      if (index > -1) {
        state.nodes.splice(index, 1);
      }
      state.edges = state.edges.filter((item) => !sameId(item.to, id) && !sameId(item.from, id));
    },
    removeEdge: (state, action) => {
      const id = action.payload;
      const index = state.edges.findIndex((item) => sameId(item.id, id));
      if (index > -1) {
        const removed = state.edges[index];
        state.edges.splice(index, 1);
        // straighten what is left between the same pair of nodes
        if (!isSelfLoop(removed)) {
          state.edges.forEach((edge, edgeIndex) => {
            if (!isSelfLoop(edge) && connectsPair(edge, removed.from, removed.to)) {
              state.edges[edgeIndex].roundness = 0;
            }
          });
        }
      }
    },
    reset: () => initialModelState,
    setMeta: (state, action) => {
      state.meta = action.payload;
    },
    setNodePositions: (state, action) => {
      state.nodes.forEach((node) => {
        const position = action.payload[idKey(node.id)];
        if (position) {
          node.x = position.x;
          node.y = position.y;
        }
      });
    },
  },
});

export const {
  setNetwork,
  setNodes,
  setEdges,
  addNode,
  addEdge,
  updateNode,
  updateEdge,
  removeNode,
  removeEdge,
  reset,
  setMeta,
  setNodePositions,
} = networkUndoSlice.actions;
export default networkUndoSlice.reducer;
